import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import { getFactoryList } from 'api/deviceApi';
import { PermissionFormData } from 'api/lotoManagementApi';
import { factoryKeys } from 'api/quires/queryKeys';
import UserSearchModal from 'components/admin/modal/UserSearchModal';
import ErrorMessage from 'components/widget/ErrorMessage';
import { User } from 'model/User';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { YN_TYPE } from 'types/enumType';
import { isSuperAdmin } from 'util/commonUtil';
import { decryptId, decryptString } from 'util/encryptUtil';

import { permissionSchema } from '../resolvers/PermissionResolvers';

type LotoPermissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: PermissionFormData) => void;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 0,
  },
};

export default function LotoPermissionModal({
  isOpen,
  onClose,
  onSubmit,
}: LotoPermissionModalProps) {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);

  const { local, factoryId, role: encryptRole } = useRecoilValue(userInfoState);
  const role = decryptString(encryptRole);
  const isSuper = isSuperAdmin(role, decryptId(factoryId ?? ''));

  const { data: factoryResp } = useQuery({
    queryKey: factoryKeys.detail(local),
    queryFn: () => getFactoryList(local),
    enabled: isSuper,
    cacheTime: 600000,
    staleTime: 600000,
  });

  const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);
  const onOpenUserSearchModal = () => {
    setIsUserModalOpen(true);
  };

  const onCloseUserSearchModal = () => {
    setIsUserModalOpen(false);
  };

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({ resolver: permissionSchema });

  useEffect(() => {
    if (isOpen) {
      reset({
        lotoYn: YN_TYPE.Y,
        selfWorkYn: YN_TYPE.Y,
        factoryId: decryptId(factoryId ?? ''),
      });

      setTimeout(() => {
        setFocus('name');
      }, 100);
    }
  }, [isOpen, reset]);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const watchSelfWork = watch('selfWorkYn');

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setValue('coopInfo', `${user.coopName}(${user.name})`);
    setValue('coopUserId', user.userId);

    onCloseUserSearchModal();
  };

  useEffect(() => {
    if (watchSelfWork === YN_TYPE.Y) {
      setSelectedUser(null);

      reset((prev) => ({
        ...prev,
        // coopWorker: '',
        coopInfo: t('validation.selectCoop'),
        coopUserId: undefined,
      }));
    }
  }, [watchSelfWork]);

  return (
    <>
      <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
        <div id={'popup-wrap'} ref={popupRef}>
          <div className={'popup-wrap'} id={'lotoPermission-detail'}>
            <div className={'popup-head'}>
              <p className={'poptit'}>{t('orderManagement.orderRegist')}</p>
            </div>
            <div className={'popup-cont'}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className={'cont'}>
                  <div className={'tbl-wrap'}>
                    <table className={'tbl_hl01'}>
                      <colgroup>
                        <col width={'25%'} />
                        <col width={''} />
                      </colgroup>
                      <tbody>
                        {isSuper ? (
                          <tr>
                            <th>{t('home.plant')}</th>
                            <td>
                              <select
                                id={''}
                                className={'w100 pad5a'}
                                {...register('factoryId')}
                              >
                                <option value={''}>
                                  {t('orderManagement.choice')}
                                </option>
                                {factoryResp?.list?.map((factory) => (
                                  <option value={factory.id} key={factory.id}>
                                    {factory.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ) : (
                          <input type={'hidden'} {...register('factoryId')} />
                        )}
                        <tr>
                          <th>{t('orderManagement.orderTitle')}</th>
                          <td>
                            <input
                              type={'text'}
                              className={'w100'}
                              {...register('name')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('device.deviceName')}</th>
                          <td>
                            <input
                              type={'text'}
                              className={'w100'}
                              {...register('equipName')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('conclusion.work')}</th>
                          <td>
                            <textarea
                              id={''}
                              cols={30}
                              rows={10}
                              className={'w100p'}
                              {...register('detail')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('conclusion.workDate')}</th>
                          <td>
                            <input
                              type={'date'}
                              className={'w100'}
                              {...register('workDate')}
                            />
                          </td>
                        </tr>
                        <tr hidden>
                          <th>{t('orderManagement.settingOrNot')}</th>
                          <td>
                            <ul className={'td-radio'}>
                              <li>
                                <input
                                  type={'radio'}
                                  id={'setting_use'}
                                  value={YN_TYPE.Y}
                                  {...register('lotoYn')}
                                />
                                <label htmlFor={'setting_use'}>
                                  {YN_TYPE.Y}
                                </label>
                              </li>
                              <li>
                                <input
                                  type={'radio'}
                                  id={'setting_notUse'}
                                  value={YN_TYPE.N}
                                  {...register('lotoYn')}
                                />
                                <label htmlFor={'setting_notUse'}>
                                  {YN_TYPE.N}
                                </label>
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>{t('coop.workYn')}</th>
                          <td>
                            <ul className={'td-radio'}>
                              <li>
                                <input
                                  type={'radio'}
                                  id={'selfWork_use'}
                                  value={YN_TYPE.Y}
                                  {...register('selfWorkYn')}
                                />
                                <label htmlFor={'selfWork_use'}>
                                  {t('coop.homeWork')}
                                </label>
                              </li>
                              <li>
                                <input
                                  type={'radio'}
                                  id={'selfWork_notUse'}
                                  value={YN_TYPE.N}
                                  {...register('selfWorkYn')}
                                />
                                <label htmlFor={'selfWork_notUse'}>
                                  {t('coop.coopWork')}
                                </label>
                              </li>
                            </ul>
                          </td>
                        </tr>
                        {watchSelfWork === YN_TYPE.N && (
                          <>
                            <tr>
                              <th>{t('coop.coopInfo')}</th>
                              <td>
                                <div className={''}>
                                  <span className={'mg15r'}>
                                    {getValues('coopInfo')}
                                  </span>
                                  <button
                                    type={'button'}
                                    className={'orange_white_btn'}
                                    onClick={() => onOpenUserSearchModal()}
                                  >
                                    {t('button.set')}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {/* <tr>
                              <th>{t('coop.workNames')}</th>
                              <td>
                                <input
                                  type={'text'}
                                  className={'w100'}
                                  {...register('coopWorker')}
                                />
                              </td>
                            </tr> */}
                          </>
                        )}
                      </tbody>
                    </table>
                    <ErrorMessage errors={errors} />
                  </div>
                </div>
                <div className={'btn-wrap'}>
                  <button type={'submit'} className={'orange_white_btn'}>
                    {t('button.save')}
                  </button>
                  <button
                    type={'button'}
                    className={'gray_white_btn'}
                    onClick={onClose}
                  >
                    {t('button.close')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ReactModal>
      {/* 사용자 검색 모달 */}
      {isUserModalOpen && (
        <UserSearchModal
          isOpen={isUserModalOpen}
          onSelect={handleSelectUser}
          onClose={onCloseUserSearchModal}
          inHouseYn={YN_TYPE.N}
        />
      )}
    </>
  );
}
